import React, { useEffect, useState } from 'react';

import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  Typography,
} from '@mui/material';

import {
  IIPENNotaryNewRegistrationObj,
  IIPENNotaryProfile,
  IIPENNotaryWholeRegObj,
} from '../../../../types/Notary';
import { IOffices, IVirtualizedOffice } from '../../../../types/Office';
import { IOrder } from '../../../../types/Order';
import { RegionDetails } from '../../../constants/RegionDetails';
import { constructPhoneObject } from '../../../utils/helpers';
import {
  ThirdPartyObject,
  isNotaryValid,
  isOneNotaryFieldFilled,
} from '../../../utils/inviteNotaryHelper';
import BranchSelector from '../../BranchSelector';
import ConformXNotarySelector from '../../ConformXNotarySelector';

export interface IChangeNotaryProps {
  open: boolean;
  onClose?: () => void;
  title: string;
  order: IOrder;
  isLoading?: boolean;
  isReassigningNotary?: boolean;
  isSending?: boolean;
  changeNotary: (data: { OrderId?: string; NotaryEmail?: string }) => void;
  addNotary: (data: IIPENNotaryNewRegistrationObj) => void;
}

const AssignOrChangeNotaryForOrder = (props: IChangeNotaryProps) => {
  const {
    order,
    isLoading,
    onClose,
    open,
    title,
    isReassigningNotary,
    isSending,
    changeNotary,
    addNotary,
  } = props;

  const branchOptions: IOffices['RegionOffices'] = RegionDetails;

  const emptyNotary: ThirdPartyObject = {
    Email: {
      Value: '',
      Error: '',
    },
    FirstName: {
      Value: '',
      Error: '',
    },
    LastName: {
      Value: '',
      Error: '',
    },
    PhoneNumber: {
      Value: '',
      Error: '',
    },
  };

  // State
  const [selectedNewNotary, setSelectedNewNotary] =
    useState<IIPENNotaryProfile | null>(null);
  const [selectedBranch, setSelectedBranch] =
    useState<IVirtualizedOffice | null>(null);
  const [isSubmitButtonDisabled, setIsSubmitButtonDisabled] =
    useState<boolean>(true);
  const [showUpdateButton, setShowUpdateButton] = useState<boolean>(true);
  const [notaryToInvite, setNotaryToInvite] =
    useState<ThirdPartyObject>(emptyNotary);

  useEffect(() => {
    if (isNotaryValid(notaryToInvite)) {
      setShowUpdateButton(false);
      setIsSubmitButtonDisabled(false);
    } else if (isOneNotaryFieldFilled(notaryToInvite)) {
      setShowUpdateButton(false);
    } else {
      setShowUpdateButton(true);
      setIsSubmitButtonDisabled(true);
    }
  }, [notaryToInvite]);

  useEffect(() => {
    if (selectedNewNotary && selectedNewNotary.Id) {
      setShowUpdateButton(true);
      setIsSubmitButtonDisabled(false);
    } else if (selectedNewNotary && selectedNewNotary.FullName) {
      setShowUpdateButton(true);
      setIsSubmitButtonDisabled(false);
    } else {
      setIsSubmitButtonDisabled(true);
      setNotaryToInvite(emptyNotary);
    }
  }, [selectedNewNotary]);

  const handleClickClose = () => {
    if (onClose) {
      onClose();
    }
  };

  const updateNotaryClicked = () => {
    if (selectedNewNotary) {
      changeNotary({
        OrderId: order.Id,
        NotaryEmail: selectedNewNotary.Email,
      });
    }
  };

  const inviteNotaryClicked = () => {
    const ipenNotary: IIPENNotaryWholeRegObj[] = [
      {
        Id: Math.random(),
        Email: notaryToInvite.Email?.Value,
        FirstName: notaryToInvite.FirstName?.Value,
        LastName: notaryToInvite.LastName?.Value,
        PhoneNumber: constructPhoneObject(notaryToInvite.PhoneNumber?.Value),
      },
    ];
    const data: IIPENNotaryNewRegistrationObj = {
      Notaries: ipenNotary,
      OrderId: order.Id,
    };
    addNotary(data);
  };

  return (
    <>
      <Dialog
        fullWidth
        maxWidth='md'
        open={open}
        scroll='paper'
        aria-labelledby='form-dialog-title'>
        <DialogTitle id='form-dialog-title'>{title}</DialogTitle>
        <>
          {(isReassigningNotary || isSending) && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
                height: '200px',
              }}>
              <Typography variant='h6'>
                {isReassigningNotary
                  ? 'Reassigning Notary...'
                  : isSending
                  ? 'Inviting Notary...'
                  : ''}
              </Typography>
              <CircularProgress />
            </Box>
          )}
          {!isReassigningNotary && !isSending && (
            <>
              <DialogContent dividers>
                <Grid container spacing={2} mb={2}>
                  <Grid item xs={12}>
                    <FormControl
                      className='full-width'
                      margin='normal'
                      variant='standard'>
                      <BranchSelector
                        regionsList={branchOptions}
                        changeValue={(value: any) => {
                          setSelectedBranch(value);
                          setSelectedNewNotary(null);
                        }}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl
                      className='full-width'
                      margin='normal'
                      variant='standard'>
                      <ConformXNotarySelector
                        initialLimit={15}
                        changeValue={(value: any) =>
                          setSelectedNewNotary(value)
                        }
                        required
                        isLoading={isLoading}
                        selectedBranch={selectedBranch}
                        setNotaryToInvite={setNotaryToInvite}
                        setSelectedNewNotary={setSelectedNewNotary}
                        setSelectedBranch={setSelectedBranch}
                        fieldsLayout={'row'}
                      />
                    </FormControl>
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions sx={{ justifyContent: 'space-between' }}>
                <Button onClick={handleClickClose} variant='text'>
                  Cancel
                </Button>
                <Button
                  variant='contained'
                  color='primary'
                  disabled={isSubmitButtonDisabled}
                  onClick={
                    showUpdateButton ? updateNotaryClicked : inviteNotaryClicked
                  }>
                  {showUpdateButton ? 'Update Notary' : 'Invite Notary'}
                </Button>
              </DialogActions>
            </>
          )}
        </>
      </Dialog>
    </>
  );
};

export default AssignOrChangeNotaryForOrder;
